/*
 * Pure helpers for the per-domain notes / changes / follow-ups timeline
 * (backend/domain_timeline.py). Dates are 'YYYY-MM-DD' strings, compared and
 * stepped in UTC so a chart bucket never shifts with the viewer's time zone.
 */

export const KINDS = [
  { key: 'note', label: 'Note', badge: 'info' },
  { key: 'change', label: 'Change', badge: 'success' },
  { key: 'follow-up', label: 'Follow-up', badge: 'warn' },
];

export const kindInfo = (kind) => KINDS.find((k) => k.key === kind) || { key: kind, label: kind || 'Entry', badge: '' };

export const todayIso = (now = new Date()) => now.toISOString().slice(0, 10);

const toDay = (iso) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86400000;

/* Whole days from a to b (b - a). */
export const daysBetween = (a, b) => Math.round(toDay(b) - toDay(a));

export const addDays = (iso, n) => new Date((toDay(iso) + n) * 86400000).toISOString().slice(0, 10);

/* Same rule as the backend's follow_up_state: overdue | due (today) | upcoming | done; null for other kinds. */
export const followUpState = (entry, today = todayIso()) => {
  if (!entry || entry.kind !== 'follow-up') return null;
  if (entry.status === 'done') return 'done';
  if (!entry.due_date) return 'upcoming';
  if (entry.due_date < today) return 'overdue';
  return entry.due_date === today ? 'due' : 'upcoming';
};

export const followUpLabel = (entry, today = todayIso()) => {
  const state = followUpState(entry, today);
  if (!state) return null;
  if (state === 'done') return { state, text: 'Done', badge: 'success' };
  const d = daysBetween(today, entry.due_date);
  if (state === 'overdue') return { state, text: `Overdue ${-d}d (due ${entry.due_date})`, badge: 'danger' };
  if (state === 'due') return { state, text: 'Due today', badge: 'warn' };
  return { state, text: `Due ${entry.due_date}${d <= 7 ? ` (in ${d}d)` : ''}`, badge: 'scheduled' };
};

/* Newest first by the date it happened, then by when it was written. */
export const sortEntries = (entries) => [...(entries || [])].sort((a, b) => {
  const k = (e) => `${e.date || ''}|${e.created_at || ''}`;
  return k(b).localeCompare(k(a));
});

export const normalizeSlug = (s) => {
  let v = String(s || '');
  try { v = decodeURIComponent(v); } catch (e) { /* keep as is */ }
  return v.replace(/^\/+|\/+$/g, '').trim();
};

/* A page's view: its own entries plus the site-wide ones. */
export const forPage = (entries, slug) => {
  const s = normalizeSlug(slug);
  if (!s) return entries || [];
  return (entries || []).filter((e) => e.scope !== 'page' || e.page === s);
};

/* Open follow-ups first (soonest due), then the rest newest first. */
export const groupForPanel = (entries, today = todayIso()) => {
  const open = (entries || []).filter((e) => e.kind === 'follow-up' && e.status !== 'done')
    .sort((a, b) => String(a.due_date || '9999').localeCompare(String(b.due_date || '9999')));
  const openIds = new Set(open.map((e) => e.id));
  return { open, rest: sortEntries((entries || []).filter((e) => !openIds.has(e.id))), overdue: open.filter((e) => followUpState(e, today) === 'overdue').length };
};

/* "label | url" or a bare url per line -> [{label, url}]. */
export const parseLinks = (text) => String(text || '').split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
  const i = line.lastIndexOf('|');
  if (i > -1) return { label: line.slice(0, i).trim() || null, url: line.slice(i + 1).trim() };
  return { label: null, url: line };
}).filter((l) => l.url);

export const authorLabel = (author) => {
  if (!author) return '';
  if (author.type === 'agent') return author.agent ? `${author.agent} (agent, ${author.name})` : `Agent (${author.name})`;
  return author.name || author.email || '';
};

/* Client-side check before posting; returns an error message or null. */
export const validateDraft = (draft) => {
  if (!String(draft.text || '').trim()) return 'Write something first.';
  if (draft.kind === 'follow-up' && !/^\d{4}-\d{2}-\d{2}$/.test(draft.due_date || '')) return 'A follow-up needs a due date.';
  return null;
};

/* Chart flags: change and note entries inside a trend window of n buckets of
   `bucket` days starting at `start`, grouped per bucket index. Follow-ups are
   plans, not events, so they are not flagged. Page entries count only when
   `slug` is given and matches (or always, with allPages). */
export const trendMarkers = (entries, start, bucket, n, { slug = null, allPages = true } = {}) => {
  if (!start || !n) return [];
  const byIdx = new Map();
  (entries || []).forEach((e) => {
    if (!e.date || (e.kind !== 'change' && e.kind !== 'note')) return;
    if (e.scope === 'page' && !allPages && e.page !== normalizeSlug(slug)) return;
    const idx = Math.floor(daysBetween(start, e.date) / (bucket || 1));
    if (idx < 0 || idx >= n) return;
    if (!byIdx.has(idx)) byIdx.set(idx, []);
    byIdx.get(idx).push(e);
  });
  return [...byIdx.entries()].sort((a, b) => a[0] - b[0]).map(([index, list]) => ({
    index,
    date: addDays(start, index * (bucket || 1)),
    entries: sortEntries(list),
    kind: list.some((e) => e.kind === 'change') ? 'change' : 'note',
  }));
};

/* First line of the markdown, stripped of the commonest markup, for a tooltip. */
export const summaryLine = (text, max = 90) => {
  const line = String(text || '').split('\n').map((l) => l.trim()).find(Boolean) || '';
  const plain = line.replace(/^#+\s*|^[-*]\s+/, '').replace(/[*_`]/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  return plain.length > max ? `${plain.slice(0, max - 1)}…` : plain;
};
